"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "@/components/common/Image";
import { Button } from "@/components/actions/Button";
import { Heading } from "@/components/ui/Heading";
import RichText from "@/components/common/RichText";
import { appendUtmsToUrl, sendGAEvent } from "@/lib/analytics";
import { getEbookFormConfig } from "@/features/defendr/ebookConfig";
import Script from "next/script";
import { usePathname, useRouter } from "next/navigation";
import Logo from "../icons/Logo";

type StatusState = {
  message: string;
  variant: "info" | "success" | "error";
};

type EbookFormBlockProps = {
  // Using any to align with current BlockProps not including image/text
  block: any;
  padding: string;
  bgColor: string;
  backgroundColorText: string;
};

export const EbookFormBlock: React.FC<EbookFormBlockProps> = ({
  block,
  padding,
  bgColor,
  backgroundColorText,
}) => {
  const pathname = usePathname();
  const router = useRouter();
  const ebookConfig = getEbookFormConfig(block?.pageHandle, pathname);
  const ebookDownloadUrl = ebookConfig.downloadUrl;
  const formElementId = `ebook-form-${ebookConfig.formId.split("-")[0]}`;
  const triggerPdfDownload = async () => {
    try {
      const pdfResponse = await fetch(ebookDownloadUrl);
      if (!pdfResponse.ok) {
        throw new Error(`Failed to fetch PDF: ${pdfResponse.status}`);
      }

      const blob = await pdfResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = ebookConfig.downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (downloadError) {
      console.error(
        "Automatic download failed, opening PDF in new tab:",
        downloadError,
      );
      window.open(ebookDownloadUrl, "_blank");
      return false;
    }
  };

  const imageUrl = block?.image?.[0]?.cdn_url.includes("?")
    ? `${block?.image?.[0]?.cdn_url}&auto=webp`
    : `${block?.image?.[0]?.cdn_url}?auto=webp`;
  const title = block?.title;
  const textJson = block?.text?.json;
  const anchor = block?.anchor || "";
  const [phone, setPhone] = useState("");
  const [formCaptureReady, setFormCaptureReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [status, setStatus] = useState<StatusState | null>(null);
  const statusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusStyles: Record<StatusState["variant"], string> = {
    info: "bg-blue-50 border-blue-200 text-blue-800",
    success: "bg-green-50 border-green-200 text-green-800",
    error: "bg-red-50 border-red-200 text-red-800",
  };

  // Check if user should be redirected to confirmation page on acousticsguide path
  useEffect(() => {
    if (ebookConfig.confirmationPath && pathname === "/acousticsguide") {
      // Check if there's a session flag indicating successful form submission
      const formSubmitted = sessionStorage.getItem("ebook-form-submitted");
      if (formSubmitted === "true") {
        // Clear the flag and redirect to confirmation page
        sessionStorage.removeItem("ebook-form-submitted");
        router.push(ebookConfig.confirmationPath);
      }
    }
  }, [ebookConfig.confirmationPath, pathname, router]);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, "");

    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  useEffect(() => {
    if (!formCaptureReady) return;

    const initFormCapture = () => {
      const d365 = (window as any).d365mktformcapture;
      if (!d365) return;

      d365
        .waitForElement(`#${formElementId}`)
        .then((form: HTMLFormElement) => {
          const mappings = [
            { FormFieldName: "firstname", DataverseFieldName: "firstname" },
            { FormFieldName: "lastname", DataverseFieldName: "lastname" },
            { FormFieldName: "companyname", DataverseFieldName: "companyname" },
            { FormFieldName: "jobtitle", DataverseFieldName: "jobtitle" },
            {
              FormFieldName: "emailaddress1",
              DataverseFieldName: "emailaddress1",
            },
            { FormFieldName: "telephone1", DataverseFieldName: "telephone1" },
            {
              FormFieldName: "consentCheckbox",
              DataverseFieldName: "consentCheckbox",
            },
            {
              FormFieldName: "mc_leadsource",
              DataverseFieldName: "mc_leadsource",
            },
            { FormFieldName: "mc_url", DataverseFieldName: "mc_url" },
          ];

          form.addEventListener(
            "submit",
            async (e) => {
              e.preventDefault();
              setIsSubmitting(true);
              setStatus({
                variant: "info",
                message: "Submitting your information...",
              });
              if (statusTimeoutRef.current) {
                clearTimeout(statusTimeoutRef.current);
              }

              // Update hidden URL field with UTMs before serialization
              const urlField = form.querySelector(
                'input[name="mc_url"]',
              ) as HTMLInputElement;
              if (urlField && typeof window !== "undefined") {
                urlField.value = appendUtmsToUrl(window.location.href);
              }

              // Clean phone number before submission
              const phoneField = form.querySelector(
                'input[name="telephone1"]',
              ) as HTMLInputElement;
              if (phoneField) {
                phoneField.value = phoneField.value.replace(/\D/g, "");
              }

              // Serialize form using FormCapture
              const serializedForm = d365.serializeForm(form, mappings);
              const builtPayload = serializedForm.SerializedForm.build();

              // The build() method wraps the actual payload in { contentType, data }
              // where data is a stringified JSON, so we need to parse it
              const payload = JSON.parse(builtPayload.data);

              // Submit through our API route to avoid CORS
              try {
                const response = await fetch("/api/submit-form", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    formId: ebookConfig.formId,
                    captureData: payload,
                  }),
                });

                const result = await response.json();

                if (response.ok && result.success) {
                  // Track successful submission
                  sendGAEvent("form_submit", "ebook_form", "submit");

                  // Show success message and clear form
                  form.reset();
                  setPhone("");
                  setIsSubmitted(true);

                  // Set session flag for acousticsguide path before redirect
                  if (
                    ebookConfig.confirmationPath &&
                    pathname === "/acousticsguide"
                  ) {
                    sessionStorage.setItem("ebook-form-submitted", "true");
                    router.push(ebookConfig.confirmationPath);
                    return;
                  }

                  // For other paths, trigger direct PDF download
                  setStatus({
                    variant: "info",
                    message: "Preparing your download...",
                  });
                  const downloadSucceeded = await triggerPdfDownload();
                  setStatus({
                    variant: "success",
                    message: downloadSucceeded
                      ? "Your download should begin automatically."
                      : "We opened the file in a new tab. If nothing happens, please use the download link shown in your browser.",
                  });

                  // Hide status message after 7 seconds
                  statusTimeoutRef.current = setTimeout(
                    () => setStatus(null),
                    7000,
                  );
                } else {
                  console.error("Form submission failed:", result);
                  setStatus({
                    variant: "error",
                    message:
                      "There was an error submitting the form. Please try again.",
                  });
                }
              } catch (error) {
                console.error("Form submission error:", error);
                setStatus({
                  variant: "error",
                  message:
                    "There was an error submitting the form. Please try again.",
                });
              } finally {
                setIsSubmitting(false);
              }
            },
            true,
          );
        })
        .catch((error: Error) => {
          console.error("Form capture initialization error:", error);
        });
    };

    initFormCapture();
  }, [
    ebookConfig.confirmationPath,
    ebookConfig.formId,
    formElementId,
    formCaptureReady,
    pathname,
    router,
  ]);

  return (
    <section
      className={` ${ebookConfig.confirmationPath && pathname === "/acousticsguide" ? "pt-0 pb-24" : padding} ${bgColor} ${backgroundColorText}  relative`}
      id={anchor || "contact"}
    >
      <div className="px-gutter 2xl:px-0 container mx-auto ">
        {isSubmitted ? (
          <div className="stack gap-8 items-center mt-24 md:mt-0  max-w-prose mx-auto">
            <svg
              width="80"
              viewBox="0 0 135 135"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_1198_6)">
                <path
                  d="M114.976 69.3848H50.7769L114.976 114.684V69.3848Z"
                  fill="#B7974A"
                />
                <path
                  d="M-0.000488281 134.082H50.7771V69.3848V0H-0.000488281V134.082Z"
                  fill="#B7974A"
                />
                <path
                  d="M134.082 58.7822V0H50.7769L134.082 58.7822Z"
                  fill="#B7974A"
                />
              </g>
              <defs>
                <clipPath id="clip0_1198_6">
                  <rect width="134.082" height="134.082" fill="white" />
                </clipPath>
              </defs>
            </svg>

            <h2 className="text-h3 text-current font-hero font-400">
              {ebookConfig.successTitle}
            </h2>
            <p className="mt-0">{ebookConfig.successBody}</p>
            {ebookConfig.learnMoreHref && (
              <Button href={ebookConfig.learnMoreHref}>
                Learn More About Us
              </Button>
            )}
            <Logo className="mt-12" />
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-start">
            <div className="flex flex-col xl:flex-row gap-6 lg:gap-12 items-start">
              {imageUrl && (
                <Image
                  src={imageUrl}
                  alt={title}
                  className="flex-shrink-0"
                  imgClassName="object-contain aspect-auto w-full lg:max-w-[300px]"
                  maxWidth="300"
                  cover={false}
                />
              )}
              <div className="stack gap-3 md:gap-6">
                <h2 className="text-h3 text-current font-hero font-400">
                  {title}
                </h2>
                <div className="h-[5px] bg-gold w-[50px]" />
                <RichText json={textJson} />
              </div>
            </div>
            <div className="relative">
              <form
                id={formElementId}
                className="stack gap-6"
                aria-busy={isSubmitting}
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="stack gap-2">
                    <label
                      htmlFor="firstname-1669975306869"
                      className="font-semibold"
                    >
                      First Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      id="firstname-1669975306869"
                      name="firstname"
                      type="text"
                      placeholder="Enter your first name"
                      required
                      maxLength={50}
                      className="w-full border border-gold rounded px-3 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                  </div>

                  <div className="stack gap-2">
                    <label
                      htmlFor="lastname-1669976849422"
                      className="font-semibold"
                    >
                      Last Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      id="lastname-1669976849422"
                      name="lastname"
                      type="text"
                      placeholder="Enter your last name"
                      required
                      maxLength={50}
                      className="w-full border border-gold rounded px-3 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                  </div>

                  {/* <div className="stack gap-2">
                <label htmlFor="companyname-1758653132387" className="font-semibold">
                  Company Name <span className="text-red-600">*</span>
                </label>
                <input
                  id="companyname-1758653132387"
                  name="companyname"
                  type="text"
                  placeholder="Enter your company name"
                  required
                  maxLength={100}
                  className="w-full border border-gold rounded px-3 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-gold"
                />
              </div>

              <div className="stack gap-2">
                <label htmlFor="jobtitle-1759256944778" className="font-semibold">
                  Job Title <span className="text-red-600">*</span>
                </label>
                <input
                  id="jobtitle-1759256944778"
                  name="jobtitle"
                  type="text"
                  placeholder="Job Title"
                  required
                  maxLength={100}
                  className="w-full border border-gold rounded px-3 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-gold"
                />
              </div> */}

                  <div className="stack gap-2">
                    <label
                      htmlFor="emailaddress1-1669977007902"
                      className="font-semibold"
                    >
                      Work Email <span className="text-red-600">*</span>
                    </label>
                    <input
                      id="emailaddress1-1669977007902"
                      name="emailaddress1"
                      type="email"
                      placeholder="Enter your work email address"
                      required
                      // pattern="[^@\s\\&quot;&lt;&gt;\)\(\[\]:;,.]+(([.]{1}[^@\s\\&quot;&lt;&gt;\)\(\[\]:;,.]+)+?|)@([^@\s\\&quot;&lt;&gt;\)\(\[\]\+:;,\.\-]+(((\.|\+|-|--)[^@\s\\&quot;&lt;&gt;\)\(\[\]+:;,.\-]+)+?|)([.][^0-9@\s\\&quot;&lt;&gt;\)\(\[\]+:;,.\-]+)+?)"
                      className="w-full border border-gold rounded px-3 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                  </div>

                  {/* <div className="stack gap-2">
                <label htmlFor="telephone1-1758653113095" className="font-semibold">
                  Phone Number <span className="text-red-600">*</span>
                </label>
                <input
                  id="telephone1-1758653113095"
                  name="telephone1"
                  type="tel"
                  placeholder="(555) 555-5555"
                  required
                  value={phone}
                  onChange={handlePhoneChange}
                  pattern="^\\(\\d{3}\\)\\s\\d{3}-\\d{4}$"
                  title="Please enter a 10-digit phone number"
                  className="w-full border border-gold rounded px-3 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-gold"
                />
                
              </div> */}
                </div>

                <input
                  type="hidden"
                  id="mc_leadsource-1759170803820"
                  name="mc_leadsource"
                  value="948100080"
                />
                <input type="hidden" id="mc_url-1759170829376" name="mc_url" />

                <div className="grid md:grid-cols-2 gap-6 lg:gap-12 items-start">
                  <div className="flex items-center gap-4">
                    <input
                      type="checkbox"
                      id="consentCheckbox-1759410030253"
                      name="consentCheckbox"
                      className="w-5 h-5 border-2 border-gold rounded bg-white text-gold focus:ring-2 focus:ring-gold"
                    />
                    <label
                      htmlFor="consentCheckbox-1759410030253"
                      className="text-sm"
                    >
                      I agree to receive training and CEU opportunities, product
                      updates, and relevant communications from Forte Opening
                      Solutions.
                    </label>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      aria-disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Download"}
                    </Button>
                  </div>
                </div>
                {status && (
                  <div
                    role="status"
                    aria-live="polite"
                    className={`mb-4 p-4 border rounded text-sm ${statusStyles[status.variant]}`}
                  >
                    {status.message}
                  </div>
                )}
              </form>
              <Script
                src="https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/usa/FormCapture/FormCapture.bundle.js"
                strategy="lazyOnload"
                onReady={() => setFormCaptureReady(true)}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
